import React, { useMemo, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import Table from './Table';
import Button from './Button';
import Container from './Container';
import formatNumber from '../utils/formatNumber';

import config from '../config';

import timezone from 'dayjs/plugin/timezone';
import clsx from 'clsx';
import Spinner from './Spinner';

dayjs.extend(timezone);
dayjs.tz.setDefault('America/New_York');

const formatData = (flips) => {
  // group flips by choiceOfToken
  const groupedFlips = flips.reduce((acc, flip) => {
    if (!acc[flip.choiceOfToken]) {
      acc[flip.choiceOfToken] = [];
    }
    acc[flip.choiceOfToken].push(flip);
    return acc;
  }, {});

  const formatted = Object.keys(groupedFlips).map((flip) => {
    return {
      token: config.flipTokenChoices.find((choice) => choice.id === parseInt(flip))?.name,
      winPercentage: `${formatNumber(((groupedFlips[flip].filter((flip) => flip.didWin).length / groupedFlips[flip].length) * 100).toFixed(0))}%`,
      totalFlips: groupedFlips[flip].length,
      volume: groupedFlips[flip].reduce((acc, flip) => acc + flip.betAmount, 0),
    };
  });

  return formatted;
};

const StatsTable = () => {
  const [timeFrame, setTimeFrame] = useState('24H');
  const [flips, setFlips] = useState([]);
  const [loading, setLoading] = useState(true);

  const onFetchFlips = async () => {
    const subtractBy = timeFrame === '24H' ? 1 : 7;
    const after = dayjs().subtract(subtractBy, 'day').format('YYYY-MM-DD');

    const { data } = await axios.get('/api/flips/stats', {
      params: {
        after,
      },
      headers: {
        Authorization: process.env.NEXT_PUBLIC_API_KEY,
      },
    });
    setLoading(false);
    setFlips(formatData(data));
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Token',
        accessor: 'token',
      },
      {
        Header: 'Win %',
        accessor: 'winPercentage',
      },
      {
        Header: 'Total Flips',
        accessor: 'totalFlips',
      },
      {
        Header: 'Volume',
        accessor: 'volume',
      },
    ],
    []
  );

  const data = useMemo(() => flips, [flips]);

  useEffect(() => {
    onFetchFlips();
  }, [timeFrame]);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex gap-3'>
        <Button
          theme='none'
          className={clsx(
            'font-medium text-sm uppercase px-3 py-1 border-2 border-gray-600 rounded-full',
            timeFrame === '24H' && 'bg-gray-600 text-white'
          )}
          onClick={() => setTimeFrame('24H')}
        >
          24H
        </Button>
        <Button
          theme='none'
          className={clsx(
            'font-medium text-sm uppercase px-3 py-1 border-2 border-gray-600 rounded-full',
            timeFrame === '1W' && 'bg-gray-600 text-white'
          )}
          onClick={() => setTimeFrame('1W')}
        >
          1W
        </Button>
      </div>
      {loading ? (
        <div className='mt-5 flex justify-center'>
          <Spinner />
        </div>
      ) : (
        <div className='flex justify-center mt-3 m w-full mx-auto rounded-lg overflow-hidden'>
          <Table columns={columns} data={data} />
        </div>
      )}
    </div>
  );
};

export default StatsTable;
